/* eslint-disable react/no-multi-comp */
import React, { useRef, useContext, useState, useMemo } from 'react';

import { Row, Col } from '@app/ui';
import BarChart, {
    BarChartOptionProps,
} from '../../../components/charts/BarChart';
import useAgeBreakdown from '../../../components/hooks/inVenue/useAgeBreakdown';
import { InVenueContext } from '../../InVenue.context';
import { getTextColor } from '../../../utils/getTextColor';
import GenderLegend from '../common/GenderLegend';
import { shortDate, decimal } from '../../../utils/formatters';
import usePrevious from '../../../components/hooks/usePrevious';

const AgeBreakdown = () => {
    const chartRef = useRef(null);
    const [hiddenGender, setHiddenGender] = useState<'Male' | 'Female' | null>(
        null,
    );
    const {
        client,
        current: { rightsholder, season, gameDates, sections },
        selectedDate,
        isLoadingFilters,
    } = useContext(InVenueContext);

    const params = {
        client,
        rightsholder,
        season,
        gameDates: selectedDate ? [selectedDate] : gameDates,
        sectionIds: sections.map(section => section.id),
    };

    const previousParams = usePrevious(params);
    const shouldUpdate =
        !previousParams ||
        JSON.stringify(params) !== JSON.stringify(previousParams);

    const { data, isLoading } = useAgeBreakdown(params);

    const isLoadingChart = isLoading || isLoadingFilters;

    const barChart = useMemo(() => {
        const options: BarChartOptionProps = {
            xType: 'category',
            yType: 'decimal',
            isDoubled: true,
            config: {
                plugins: {
                    datalabels: {
                        display: true,
                        align: 'center',
                        anchor: 'center',
                        color: context =>
                            getTextColor(context.dataset.backgroundColor),
                        formatter: (value, context) => {
                            const total = context.dataset.data.reduce(
                                (acc, cur) => acc + cur,
                                0,
                            );
                            if (total === 0) return '';
                            if (value / total < 0.03) return '';
                            return decimal(value);
                        },
                    },
                    legend: {
                        display: false,
                    },
                    title: {
                        text: selectedDate ? shortDate(selectedDate) : '',
                        color: '#7a7a7a',
                        font: { size: 11 },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        grid: {
                            drawBorder: true,
                            lineWidth: 1,
                        },
                    },
                    y: {
                        stacked: true,
                        title: {
                            display: true,
                            text: 'Attendance',
                            align: 'center',
                            font: { size: 14 },
                        },
                        grid: {
                            display: false,
                            drawBorder: true,
                            lineWidth: 1,
                        },
                    },
                },
                animation: {
                    duration: 0,
                },
            },
        };

        const hiddenGenders = hiddenGender ? [hiddenGender] : [];

        const chartData = {
            labels: data?.labels || [],
            datasets:
                data?.datasets?.filter(dataset => {
                    // remove the s from the end of the label
                    const singularLabel = dataset.label.substring(
                        0,
                        dataset.label.length - 1,
                    );
                    return !hiddenGenders.includes(singularLabel);
                }) || [],
        };

        return (
            <BarChart
                ref={chartRef}
                data={chartData}
                options={options}
                isLoading={isLoadingChart}
            />
        );
    }, [hiddenGender, selectedDate, isLoadingChart, shouldUpdate]);

    const onLegendClick = (g: 'Male' | 'Female' | null) => {
        const newGender = g === hiddenGender ? null : g;
        setHiddenGender(newGender);
    };

    return (
        <Row gutter={16} className="invenue-flex-container">
            <Col md={24}>
                <p className="-m-b-1">
                    Attendance Distribution by Age and Gender
                </p>
            </Col>
            <Col md={24}>
                <div className="stadium-attendance-card stadium-attendance-chart-container">
                    <div className="stadium-attendance-chart-container stadium-attendance-chart-container-age">
                        {barChart}
                    </div>
                    <GenderLegend
                        isLoading={isLoadingChart}
                        onClick={onLegendClick}
                        hidden={hiddenGender}
                    />
                </div>
            </Col>
        </Row>
    );
};

export default AgeBreakdown;
